import { type TextStyle } from 'react-native';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';
import { posCartLinesStyleDefs } from './posCartLines.styleDefs';
import { posCartParkStyleDefs } from './posCartPark.styleDefs';
import { posCartPromosStyleDefs } from './posCartPromos.styleDefs';

export const posCartStyleDefs = {
  ...posCartLinesStyleDefs,
  ...posCartPromosStyleDefs,
  ...posCartParkStyleDefs,

  cartTitleCompact: {
      fontSize: 17,
      lineHeight: 21,
    },

  /** PC: panel paper Square (PaperSheet). */
  cartCanvasCard: {
      ...ticketSurfaces.paperSheet,
      padding: 0,
      gap: 0,
      flex: 1,
      minHeight: 0,
    },

  cartCanvasCardInner: {
      flex: 1,
      minHeight: 0,
      gap: spacing.sm,
    },

  cartCanvasCardCompact: {
      padding: 0,
      gap: 0,
    },

  /** Dock PC: header/foot con padding propio; paper sobre counter del dock. */
  cartCanvasCardDesk: {
      borderRadius: 0,
      borderWidth: 0,
      borderLeftWidth: 0,
      backgroundColor: ticketColors.paper,
      padding: 0,
      gap: 0,
      flex: 1,
      minHeight: 0,
    },

  cartCanvasBody: {
      gap: 0,
      flex: 1,
      minHeight: 0,
      paddingTop: spacing.sm,
  },

  cartCanvasBodyCompact: {
      gap: 0,
    },

  cartCanvasBodyDesk: {
      gap: 0,
      flex: 1,
      // Piso 0: en viewports bajos la lista cede hasta casi nada y el nest
      // «A cobrar»/«Cobrar» queda pineado al fondo de la card — nunca clipea.
      minHeight: 0,
    },

  cartEmptyPanel: {
      ...ticketSurfaces.paperSheet,
      minHeight: 54,
      borderRadius: 10,
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.sm,
      marginHorizontal: 12,
      marginTop: 12,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },

  cartEmptyIconBadge: {
      width: 34,
      height: 34,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: ticketColors.ruleSoft,
      backgroundColor: ticketColors.paper2,
      justifyContent: 'center',
      alignItems: 'center',
    },

  cartEmptyTextBlock: {
      flex: 1,
      minWidth: 0,
      gap: 1,
    },

  cartEmptyTotal: {
      ...typography.titleLg,
      color: ticketColors.ink3,
      fontWeight: '800',
      fontFamily: ticketFonts.mono,
    },

  cartEmptyHint: {
      ...typography.caption,
      color: ticketColors.ink3,
      lineHeight: 16,
      fontFamily: ticketFonts.sans,
    },

  cartEmptyShortcuts: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.xs,
      marginTop: spacing.xs,
    },

  cartEmptyShortcutBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 4,
      paddingHorizontal: spacing.sm,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: ticketColors.gold,
      backgroundColor: ticketColors.brandSoft,
    },

  cartEmptyShortcutText: {
      ...typography.caption,
      color: ticketColors.gold,
      fontWeight: '700',
      fontFamily: ticketFonts.sans,
    },

  cartHeaderTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      flex: 1,
      minWidth: 0,
    },

  cartItemCountBadge: {
      borderRadius: 8,
      borderWidth: 1,
      borderColor: ticketColors.ruleSoft,
      backgroundColor: ticketColors.paper,
      paddingHorizontal: 8,
      paddingVertical: 2,
    },

  cartItemCountBadgeText: {
      ...typography.caption,
      color: ticketColors.ink2,
      fontWeight: '700',
      fontSize: 11,
      lineHeight: 13,
      fontFamily: ticketFonts.sans,
    },

  cartItemCountBadgePulse: {
      borderColor: ticketColors.gold,
      backgroundColor: ticketColors.brandSoft,
    },

  cartHeaderDiningTable: {
      ...typography.caption,
      color: colors.textSecondary,
      fontWeight: '600' as const,
      flexShrink: 1,
      marginLeft: 'auto' as const,
    },

  cartSavingsRow: {
      alignSelf: 'stretch',
      paddingHorizontal: spacing.sm,
    },

  cartSavingsText: {
      ...typography.caption,
      color: colors.success,
      fontWeight: '700',
      fontSize: 12,
      lineHeight: 14,
      textAlign: 'right',
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
    },

  cartCheckoutFooter: {
      marginTop: spacing.sm,
      paddingTop: 0,
      paddingBottom: 12,
      paddingHorizontal: 12,
      borderTopWidth: 0,
      gap: spacing.sm,
    },

  cartCheckoutFooterSticky: {
      backgroundColor: ticketColors.paper,
    },

  cartCheckoutFooterCompact: {
      marginTop: 3,
      paddingTop: 3,
      gap: 3,
    },

  cartCheckoutFooterInline: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.sm,
    },

  cartCheckoutInlineTotalWrap: {
      flex: 1,
      minWidth: 0,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 10,
      backgroundColor: ticketColors.brandSoft,
      borderWidth: 1,
      borderColor: ticketColors.gold,
    },

  cartCheckoutInlineCta: {
      flexShrink: 0,
      alignSelf: 'auto',
      width: 'auto',
      minWidth: 112,
      paddingHorizontal: spacing.md,
    },

  posMobileCartBar: {
      borderTopWidth: 1,
      borderTopColor: ticketColors.ruleSoft,
      backgroundColor: ticketColors.paper2,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },

  posMobileCartBarSummary: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },

  posMobileCartBarSummaryText: {
      ...typography.body,
      color: colors.textPrimary,
      fontWeight: '700',
      flex: 1,
      minWidth: 0,
    },

  posMobileCartBarSummaryHint: {
      ...typography.caption,
      color: colors.textMuted,
      fontWeight: '600',
    },

  posMobileCartSheetBackdrop: {
      flex: 1,
      backgroundColor: ticketColors.dim,
      justifyContent: 'flex-end',
    },

  posMobileCartSheetPanel: {
      width: '100%',
      maxHeight: '86%',
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      borderTopWidth: 1,
      borderColor: ticketColors.ruleSoft,
      backgroundColor: ticketColors.paper2,
      paddingHorizontal: 16,
      paddingTop: 8,
      overflow: 'hidden',
    },

  posMobileCartSheetHandle: {
      alignSelf: 'center',
      width: 28,
      height: 3,
      borderRadius: 2,
      backgroundColor: ticketColors.chromeHandle,
      marginBottom: 10,
    },

  cartCheckoutFooterSheet: {
      marginTop: 0,
      paddingTop: 14,
      paddingBottom: 8,
      gap: 12,
      borderTopWidth: 1,
      borderTopColor: ticketColors.ruleSoft,
      backgroundColor: ticketColors.paper2,
      marginHorizontal: -16,
      paddingHorizontal: 18,
      flexShrink: 0,
    },

  cartCheckoutHeroTotal: {
      gap: 2,
      paddingHorizontal: 14,
      paddingVertical: 12,
      borderRadius: 10,
      backgroundColor: ticketColors.brandSoft,
      borderWidth: 1,
      borderColor: ticketColors.gold,
    },

  cartCheckoutHeroLabel: {
      color: ticketColors.ink3,
      fontWeight: '700',
      fontSize: 12,
      letterSpacing: 0.4,
      fontFamily: ticketFonts.sans,
    },

  cartCheckoutHeroValue: {
      color: ticketColors.ink,
      fontWeight: '700',
      fontSize: 36,
      lineHeight: 38,
      letterSpacing: -1.2,
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
      fontFamily: ticketFonts.mono,
    },

  cartCheckoutHeroSavings: {
      color: ticketColors.ok,
      fontWeight: '500',
      fontSize: 13,
      lineHeight: 16,
      marginBottom: 2,
      fontFamily: ticketFonts.sans,
    },

  posPrimaryCtaSheet: {
      minHeight: 52,
      borderRadius: 14,
    },

  posPrimaryCtaTextSheet: {
      fontSize: 16,
      fontWeight: '800',
      letterSpacing: -0.2,
      fontFamily: ticketFonts.sans,
    },

  cartCanvasCardSheet: {
      borderWidth: 0,
      borderRadius: 0,
      padding: 0,
      gap: spacing.sm,
      backgroundColor: 'transparent',
      flex: 1,
      minHeight: 0,
      width: '100%',
    },

  cartCanvasBodySheet: {
      flex: 1,
      minHeight: 0,
      gap: spacing.sm,
      width: '100%',
    },

  cartHeaderParkBtnSheet: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderWidth: 0,
      backgroundColor: 'transparent',
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderRadius: 8,
      minHeight: 40,
    },

  cartHeaderParkBtnTextSheet: {
      color: colors.textSecondary,
      fontWeight: '500',
      fontSize: 13,
      lineHeight: 16,
      fontFamily: ticketFonts.sans,
    },

  cartCheckoutTotalsPanel: {
      gap: spacing.xs,
      paddingHorizontal: 14,
      paddingVertical: 12,
      borderRadius: 10,
      backgroundColor: ticketColors.brandSoft,
      borderWidth: 1,
      borderColor: ticketColors.gold,
    },

  cartCheckoutTotalsPanelCompact: {
      paddingHorizontal: spacing.xs,
      gap: 2,
    },

  posPrimaryCta: {
      minHeight: 44,
      borderRadius: 10,
      backgroundColor: ticketColors.gold,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing.md,
    },

  posPrimaryCtaFull: {
      alignSelf: 'stretch',
      width: '100%',
    },

  posPrimaryCtaCompact: {
      minHeight: 38,
      borderRadius: 11,
      paddingHorizontal: spacing.sm,
    },

  posPrimaryCtaText: {
      ...typography.body,
      color: ticketColors.goldInk,
      fontWeight: '700',
      fontFamily: ticketFonts.sans,
    },

  posPrimaryCtaTextCompact: {
      fontSize: 13,
    },

  cartCollapseToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.sm,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.inputBackground,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.sm,
      marginBottom: spacing.xs,
    },

  cartCollapseToggleLabel: {
      ...typography.caption,
      color: colors.textPrimary,
      fontWeight: '800',
      flex: 1,
      minWidth: 0,
    },

  cartCollapseToggleTotal: {
      ...typography.caption,
      color: colors.textSecondary,
      fontWeight: '700',
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
    },

  // ── Nest (proto `.nest`) — total + CTA dentro del soft-teal ──
  cartCheckoutNest: {
      marginTop: 10,
      marginBottom: 12,
      padding: 14,
      borderRadius: 12,
      backgroundColor: ticketColors.brandSoft,
      borderWidth: 1,
      borderColor: ticketColors.brandBorderSoft,
      gap: 10,
    },

  /** Dock PC: nest más compacto para que «Cobrar» entre en viewports bajos. */
  cartCheckoutNestDesk: {
      marginTop: 6,
      marginBottom: 6,
      padding: 12,
      gap: 8,
    },

  cartCheckoutNestSheet: {
      marginHorizontal: 0,
    },

  cartNestTotalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'baseline',
    },

  cartNestLabel: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: ticketColors.brandInk,
      fontFamily: ticketFonts.sans,
    },

  cartNestValue: {
      fontSize: 24,
      fontWeight: '800',
      letterSpacing: -0.5,
      color: ticketColors.ink,
      fontFamily: ticketFonts.sans,
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
    },

  cartNestCta: {
      minHeight: 42,
      borderRadius: 10,
      backgroundColor: ticketColors.gold,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing.md,
    },

  cartNestCtaSheet: {
      minHeight: 52,
      borderRadius: 14,
    },

  cartNestCtaText: {
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: -0.2,
      color: ticketColors.goldInk,
      fontFamily: ticketFonts.sans,
    },
} as const;
